import { AfterViewInit, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, switchMap, tap } from 'rxjs';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { map } from 'rxjs/operators';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { makeQueryKey } from '../../utils/entity-states/query-key';
import { TestEntity } from '../test.entity';
import { TestEntityService } from '../services/test.entity.service';
import { FormDialogTestComponent } from '../form-dialog-test/form-dialog-test.component';
import { ConsumerEntityService } from '../../consumer/services/consumer.entity.service';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import { SequenceEntityService } from '../../sequence/services/sequence.entity.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { SubjectEntity } from '../../subject/subject.entity';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { messageTypes, WidgetMessageService } from '../../core/services/widget-message.service';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData
} from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { PlayerMessageService } from '../../core/services/player.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TableLoadingSpinnerComponent } from '../../utils/component/table-loading-spinner/table-loading-spinner';
import { LocalStorageService } from '../../core/services/local-storage.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { BaseFilterComponent } from '../../utils/component/base-filter-component/base-filter-component';

@Component({
  selector: 'pixl-main-test',
  templateUrl: './main-test.component.html',
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginator,
    MatButton,
    MatIcon,
    MatIconButton,
    MatTooltipModule,
    FreeFilterInputComponent,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    TableLoadingSpinnerComponent,
    TranslocoDirective,
  ],
  styleUrl: './main-test.component.scss'
})

export class MainTestComponent extends BaseFilterComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private widgetService = inject(WidgetMessageService);
  private testService = inject(TestEntityService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private sequenceService = inject(SequenceEntityService);
  private playerService = inject(PlayerMessageService);
  private dialog = inject(MatDialog);
  private refresh$ = new BehaviorSubject<void>(undefined);
  private localStorageService = inject(LocalStorageService);
  private readonly translocoService = inject(TranslocoService);

  tests$!: Observable<TestEntity[]>;
  deletedIds$ = new BehaviorSubject<string[]>([]);

  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;

  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;

  displayedColumns: string[] = ['actions', 'id', 'name', 'consumers', 'subjects', 'sequenceCount', 'sequences', 'version', 'updated', 'changedBy', 'created_at'];

  totalItems = 1000;

  private destroyRef = inject(DestroyRef);
  private readonly pageChange$ = toObservable(this.pageChange);

  ngOnInit() {
    this.loadRelationData();

    this.tests$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'consumers,subjects,sequences',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': 'name'
        };

        if (this.freeFilter?.trim()?.length >= this.freeFilterLen) {
          query['filter[idOrNameLike]'] = this.freeFilter.trim();
        }

        if (this.consumerFilter?.length > 0) {
          query['filter[consumers][id]'] = this.consumerFilter.join(',');
        }

        if (this.subjectFilter?.length > 0) {
          query['filter[subjectId]'] = this.subjectFilter.join(',');
        }

        return from(this.testService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.testService.allByQuery(query))),
          map((tests: TestEntity[]) => tests.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.testService.stateClass) as EntityStateModel<TestEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state?.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit() {
    this.paginator().page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange.set({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(
      this.subjectService.ensureLoaded(subjectQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery))
      )
    );
  }

  onAdd(): void {
    const dialogRef = this.dialog.open(FormDialogTestComponent, {
      width: '500px',
      data: {}
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<TestEntity> | undefined) => {
      if (!result) {
        return;
      }

      try {
        const newEntityInstance = Object.assign(new TestEntity(), result);
        await this.testService.create(newEntityInstance);

        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.widgetService.setMessage({
          message: this.translocoService.translate('data_saving_successful'),
          messageTye: 'mtSuccess'
        });
      }
      catch (error) {
        console.error('Erstellen fehlgeschlagen', error);
        this.widgetService.setMessage({
          message: this.translocoService.translate('data_saving_error'),
          messageTye: 'mtError'
        });
      }
    });
  }

  onEdit(test: TestEntity) {
    this.currentSelectedRow.set(test.id);
    const dialogRef = this.dialog.open(FormDialogTestComponent, {
      width: '800px',
      data: { ...test }
    });

    dialogRef.afterClosed().subscribe(async (updatedTest: TestEntity | undefined) => {
      if (!updatedTest) return;
      await firstValueFrom(this.testService.update(updatedTest));
      this.refresh$.next();

      this.showMessage('Test erfolgreich editiert.');
    });
  }

  async onCopy(test: TestEntity) {
    this.currentSelectedRow.set(test.id);
    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(test.name),
            headerText: 'Test kopieren',
            afterNameText: 'wirklich kopieren?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    const copyEntityInstance = Object.assign(new TestEntity(), test);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).id = undefined;
    copyEntityInstance.name = `${copyEntityInstance.name} (Kopie)`;
    copyEntityInstance.version = 1;
    await this.testService.create(copyEntityInstance);
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.showMessage('Test erfolgreich dupliziert.');
  }

  async onDelete(test: TestEntity) {
    this.currentSelectedRow.set(test.id);
    if (!test?.id) {
      console.error('ID is undefined!', test);
      return;
    }

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(test.name),
            headerText: 'Test löschen',
            afterNameText: 'wirklich löschen?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.testService.delete(test.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.showMessage('Test erfolgreich gelöscht.');
  }

  private showMessage(message: string, messageTye: messageTypes = 'mtSuccess'): void {
    this.widgetService.setMessage({
      message,
      messageTye
    });
  }

  getConsumerName(consumerId: string): string {
    if (!consumerId) return '---';

    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(consumerId);

    return consumer?.name || consumerId;
  }

  getSubjectName(subjectId: string): string {
    if (!subjectId) return '---';

    const state = this.store.selectSnapshot(this.subjectService.stateClass);
    const subject = this.subjectService.stateClass.byId(state)(subjectId);

    return subject?.name || subjectId;
  }

  getSequencesArray(sequences: { id: string }[]): string[] {
    if (!sequences?.length) return [];

    const state = this.store.selectSnapshot(this.sequenceService.stateClass);

    return sequences.map(lu => {
      const entity = this.sequenceService.stateClass.byId(state)(lu.id);
      return entity?.name || lu.id;
    });
  }

  public previewPlayer2(test: TestEntity) {
    this.currentSelectedRow.set(test.id);
    this.playerService.openPlayerPreview(test.consumerId!, 'sequenceGroup', test.id);
  }

  trackById(index: number, test: TestEntity): string {
    return test.id;
  }
}
